"use client";

import Link from "@/components/locale-link";
import { Checkbox } from "@/components/tool-kit/primitives";
import type { CitationStyleConverterStrings } from "@/components/tools/citation-style-converter";
import {
  Badge,
  type BadgeTone,
} from "@/components/tools/reference-tools/badge";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type BestMatch,
  doiUrl,
} from "@/lib/tool-kit/reference-tools/doi-finder";
import {
  type ConvertResult,
  GENERATOR_HREFS,
  type RefRow,
  rowDoi,
  suggestGenerator,
} from "@/lib/tool-kit/style-converter/convert";

/*
 * One reference in the Citation Style Converter: its state, the Crossref record set
 * beside it with the three checks behind the verdict, the reader's confirmation, and —
 * once the list is converted — the reference in the new style or the reason it is not.
 */

type Strings = CitationStyleConverterStrings;
type BadgeKey = keyof Strings["states"];

const TONES: Record<BadgeKey, BadgeTone> = {
  confident: "good",
  uncertain: "warn",
  none: "neutral",
  given: "info",
  short: "neutral",
  over: "neutral",
  failed: "bad",
  queued: "neutral",
  converted: "good",
  unconverted: "bad",
};

const LINK = "break-all text-primary underline-offset-4 hover:underline";
const CAPTION =
  "font-medium text-muted-foreground text-xs uppercase tracking-wide";

function badgeKey(row: RefRow, result: ConvertResult | undefined): BadgeKey {
  if (result) {
    return result.ok ? "converted" : "unconverted";
  }
  if (row.state === "done") {
    return row.match ? row.match.level : "none";
  }
  return row.state;
}

/** Rows searched but left without a confirmed DOI: they need entering by hand. */
function needsManual(row: RefRow): boolean {
  if (row.state === "queued" || row.state === "over") {
    return false;
  }
  return rowDoi(row) === null;
}

function Checks({ match, strings }: { match: BestMatch; strings: Strings }) {
  const fmt = useToolNumber();
  const { score, candidate } = match;
  const name = candidate.authors[0] ?? "";
  const year = candidate.year === null ? "" : String(candidate.year);
  const author = {
    true: t(strings.checks.authorYes, { name }),
    false: t(strings.checks.authorNo, { name }),
    null: strings.checks.authorNone,
  }[String(score.author) as "true" | "false" | "null"];
  const years = {
    exact: t(strings.checks.yearExact, { year }),
    near: t(strings.checks.yearNear, { year }),
    no: t(strings.checks.yearNo, { year }),
    null: strings.checks.yearUnknown,
  }[score.year ?? "null"];
  return (
    <ul className="list-disc space-y-1 pl-5 text-muted-foreground text-xs">
      <li>
        {t(strings.checks.title, {
          share: fmt(score.title, {
            style: "percent",
            maximumFractionDigits: 0,
          }),
        })}
      </li>
      <li>{author}</li>
      <li>{years}</li>
    </ul>
  );
}

function RecordCard({
  match,
  strings,
}: {
  match: BestMatch;
  strings: Strings;
}) {
  const { candidate } = match;
  const authors = candidate.authors.slice(0, 3).join(", ");
  const more = candidate.authors.length > 3 ? ` ${strings.etAl}` : "";
  const url = doiUrl(candidate.doi);
  return (
    <div className="space-y-1 text-sm">
      <p className="font-medium text-foreground">{candidate.title}</p>
      <p className="text-muted-foreground">
        {[`${authors}${more}`, candidate.year, candidate.container]
          .filter((part) => part !== "" && part !== null)
          .join(" · ")}
      </p>
      <a className={LINK} href={url} rel="noopener noreferrer" target="_blank">
        {url}
      </a>
    </div>
  );
}

function Outcome({
  row,
  result,
  strings,
}: {
  row: RefRow;
  result: ConvertResult | undefined;
  strings: Strings;
}) {
  if (result?.ok) {
    return (
      <p className="break-words rounded-md bg-muted p-3 text-foreground text-sm">
        {result.text}
      </p>
    );
  }
  if (result) {
    return (
      <p className="text-destructive text-xs">
        {strings.convertFailures[result.reason]}
      </p>
    );
  }
  if (row.state === "failed") {
    return (
      <p className="text-muted-foreground text-xs">
        {strings.failures[row.reason ?? "unavailable"]}
      </p>
    );
  }
  if (row.state === "done" && !row.match) {
    return (
      <p className="text-muted-foreground text-xs">{strings.notes.none}</p>
    );
  }
  if (row.state === "short" || row.state === "over") {
    return (
      <p className="text-muted-foreground text-xs">
        {strings.notes[row.state]}
      </p>
    );
  }
  return null;
}

function ManualEntry({ row, strings }: { row: RefRow; strings: Strings }) {
  const generator = suggestGenerator(row.reference);
  return (
    <p className="text-sm">
      <span className="font-medium text-foreground">{strings.manualFlag} </span>
      <Link className={LINK} href={GENERATOR_HREFS[generator]}>
        {t(strings.manualOpen, { generator: strings.generators[generator] })}
      </Link>
    </p>
  );
}

export function StyleRowCard({
  row,
  result,
  strings,
  onToggle,
}: {
  row: RefRow;
  result: ConvertResult | undefined;
  strings: Strings;
  onToggle: (id: number, accepted: boolean) => void;
}) {
  const key = badgeKey(row, result);
  const match = row.state === "done" ? row.match : null;
  const manual = needsManual(row) || (result !== undefined && !result.ok);
  return (
    <li className="space-y-3 rounded-lg border border-border bg-card p-3">
      <Badge tone={TONES[key]}>{strings.states[key]}</Badge>
      {match ? (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1">
            <p className={CAPTION}>{strings.yourReference}</p>
            <p className="break-words text-foreground text-sm">
              {row.reference}
            </p>
          </div>
          <div className="space-y-2">
            <p className={CAPTION}>{strings.crossrefRecord}</p>
            <RecordCard match={match} strings={strings} />
            <Checks match={match} strings={strings} />
            <Checkbox
              checked={row.accepted}
              label={strings.accept}
              onChange={(checked) => onToggle(row.id, checked)}
            />
          </div>
        </div>
      ) : (
        <p className="break-words text-foreground text-sm">{row.reference}</p>
      )}
      {row.state === "given" ? (
        <Checkbox
          checked={row.accepted}
          label={t(strings.useGivenDoi, { doi: row.givenDoi ?? "" })}
          onChange={(checked) => onToggle(row.id, checked)}
        />
      ) : null}
      <Outcome result={result} row={row} strings={strings} />
      {manual ? <ManualEntry row={row} strings={strings} /> : null}
    </li>
  );
}
